/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  downloadAuthenticatedFile,
  SessionExpiredError
} from 'src/utils/authenticatedFetch';

export const getFormattedPhoneNumber = (value) => {
  let formattedValue = value.replace(/\D/g, ''); // Remove non-digits
  // Format it as 111-222-3333
  if (formattedValue.length > 3 && formattedValue.length <= 6) {
    formattedValue = `${formattedValue.slice(0, 3)}-${formattedValue.slice(3)}`;
  } else if (formattedValue.length > 6) {
    formattedValue = `${formattedValue.slice(0, 3)}-${formattedValue.slice(
      3,
      6
    )}-${formattedValue.slice(6, 10)}`;
  }
  return formattedValue?.slice(0, 12);
};

export const getFileFromUrl = async (
  url: string,
  filename = 'downloadedFile'
) => {
  try {
    // Fetch the file from the URL
    const response = await fetch(url, { mode: 'cors' });
    // Check if the fetch was successful
    if (!response.ok)
      throw new Error(
        `Failed to fetch file from URL: ${response.status} ${response.statusText}`
      );
    // Convert the response to a Blob
    const blob = await response.blob();
    // Extract the MIME type from the Blob
    const mimeType = blob.type || 'application/octet-stream';
    // Create a File object from the Blob
    const file = new File([blob], filename, { type: mimeType });

    return file;
  } catch (error) {
    console.error('Error in getFileFromUrl:', error);
    throw error;
  }
};

export function ApplyDebounce(func, timeout = 300) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, timeout);
  };
}

export const requiredFieldStyle = {
  '& .MuiInputLabel-asterisk': {
    color: 'red'
  },
  mb: { xs: 2 }
};

// set data on the local storage
export const setLocalData = (keyName, keyValue) => {
  localStorage.setItem(keyName, keyValue);
};

// get data from the local storage
export const getLocalData = (keyName) => {
  return localStorage.getItem(keyName);
};

// clear local data from the local storage
export const clearLocalData = () => {
  localStorage.clear();
};

// set data on the session storage
export const setSessionData = (keyName, keyValue) => {
  sessionStorage.setItem(keyName, keyValue);
};

// get data from the session storage
export const getSessionData = (keyName) => {
  return sessionStorage.getItem(keyName);
};

// clear local data from the session storage
export const clearSessionData = () => {
  sessionStorage.clear();
};

export const convertDateToUTC = (createDate) => {
  // Create a local date object
  const targetDate = new Date(createDate);

  // Get the UTC equivalent of the local date
  const utcYear = targetDate.getUTCFullYear();
  const utcMonth = targetDate.getUTCMonth(); // Month is 0-based, so 0 = January
  const utcDate = targetDate.getUTCDate();
  const utcHours = targetDate.getUTCHours();
  const utcMinutes = targetDate.getUTCMinutes();
  const utcSeconds = targetDate.getUTCSeconds();

  // Create a new UTC date using the UTC components
  const utcDateString = new Date(
    Date.UTC(utcYear, utcMonth, utcDate, utcHours, utcMinutes, utcSeconds)
  );
  return utcDateString;
};

export function getTimeDifference(createDate) {
  // Parse the incoming createDate string into a Date object (local time)
  const createDateObj = new Date(createDate);

  // Check if createDate is valid
  // if (isNaN(createDateObj.getTime())) return "Invalid date";

  // Get the current date and time in UTC
  const now = new Date(); // This is the current local time
  const nowUtc = new Date(now.getTime() + now.getTimezoneOffset() * 60000); // Convert to UTC time

  // Convert createDate to UTC by getting its time and applying UTC offset
  const createDateUtc = new Date(
    createDateObj.getTime() + createDateObj.getTimezoneOffset() * 60000
  );

  // Calculate the time difference in milliseconds
  const timeDiff = nowUtc.getTime() - createDateUtc.getTime();

  // Time constants in milliseconds
  const msInSecond = 1000;
  const msInMinute = msInSecond * 60;
  const msInHour = msInMinute * 60;
  const msInDay = msInHour * 24;
  const msInWeek = msInDay * 7;
  const msInMonth = msInDay * 30;
  const msInYear = msInDay * 365;

  // Handle future date cases (if timeDiff < 0, it means it's a future date)
  // if (timeDiff < 0) return "Future date";

  // Calculate and return the time difference in a human-readable format
  if (timeDiff < msInSecond) {
    return 'Just now';
  }

  if (timeDiff < msInMinute) {
    const seconds = Math.floor(timeDiff / msInSecond);
    return `${seconds} ${seconds === 1 ? 'sec' : 'secs'} ago`;
  }

  if (timeDiff < msInHour) {
    const minutes = Math.floor(timeDiff / msInMinute);
    return `${minutes} ${minutes === 1 ? 'min' : 'mins'} ago`;
  }

  if (timeDiff < msInDay) {
    const hours = Math.floor(timeDiff / msInHour);
    return `${hours} ${hours === 1 ? 'hr' : 'hrs'} ago`;
  }

  if (timeDiff < msInWeek) {
    const days = Math.floor(timeDiff / msInDay);
    return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  }

  if (timeDiff < msInMonth) {
    const weeks = Math.floor(timeDiff / msInWeek);
    return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
  }

  if (timeDiff < msInYear) {
    const months = Math.floor(timeDiff / msInMonth);
    return `${months} ${months === 1 ? 'month' : 'months'} ago`;
  }

  const years = Math.floor(timeDiff / msInYear);
  return `${years} ${years === 1 ? 'year' : 'years'} ago`;
}

export const isPastDate = (dateToCheck) => {
  const currentDate = new Date()?.toLocaleDateString(); //'10/01/2025'
  const checkDate = new Date(dateToCheck)?.toLocaleDateString();
  return new Date(dateToCheck) < new Date() && currentDate !== checkDate;
};

// true - false = false /08/01/2025
// true - true = true //08/01/2025
// false - true = false

// this function declaration checks the status of application details (provided as param) and return true if matches the condition
export const shouldShowEdit = (item) => {
  const statusList = [
    'Approved',
    'Rejected',
    'Withdrawn',
    'Withdraw',
    'Approved Closed',
    'Issued',
    'Closed Out'
  ];
  return statusList?.includes(item?.ApplicationStatus);
};

//Here is a common fucntion for downloading pdf just by providing a pdf url as param
export const DownloadPDF = async (urlLink: string, filename = 'payment-receipt.pdf') => {
  try {
    await downloadAuthenticatedFile(urlLink, filename);
  } catch (error) {
    if (error instanceof SessionExpiredError) {
      return;
    }
    console.error('Error downloading PDF:', error);
  }
};
